import { Icon } from '@/components/Icon';
import { formatPrice } from '@/lib/pricing';
import { hasAnyBalance, type PlanChange as PlanChangeData } from '@/lib/billing';
import { TIERS } from '@/lib/tier';
import './PlanChange.css';

/**
 * Plan-change confirmation. Recreated from `Aware Plan Change Confirmation.dc.html`.
 *
 * Sits between plan selection and the checkout overlay — by the time the customer reaches
 * checkout, everything here has been agreed to.
 *
 * ## The rule that shapes the layout
 *
 * The pro-rated **money** credit and the carried **paid balances** are two independent facts
 * and are never netted into one number or one line. In the design they are two separate rows
 * of the card, with their own icons and their own divider. That structural separation is kept
 * here on purpose: there is no arrangement of this component in which the two could be summed
 * by accident, because they are not even the same shape in the data (`money` vs
 * `carriedBalances`).
 *
 * Combining the credit with the charge to produce "Charged today" is fine — that is ordinary
 * invoice arithmetic, and the server does it.
 *
 * ## The four states
 *
 *   upgrade + balances     the primary case
 *   downgrade + balances   same carry-over guarantee, typically $0.00 today with the remaining
 *                          credit rolling to the next invoice. A retention outcome, not a
 *                          penalty — so nothing here is framed as a loss.
 *   no balances            the carry-over row is ABSENT, not rendered as "0 questions carry
 *                          over". `null` means no balance held; `0` would be a real zero. The
 *                          data layer has to preserve that difference or this state is wrong.
 *   trial                  no proration section at all. A green banner states plainly that
 *                          nothing has been charged, with the exact date and amount of the
 *                          first charge.
 *
 * The payment provider is never named anywhere on this screen.
 */
export function PlanChange({
  change,
  onConfirm,
  onCancel,
}: {
  change: PlanChangeData;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const fromPlan = TIERS[change.fromTier];
  const toPlan = TIERS[change.toTier];
  const money = change.money;
  const showCarryOver = hasAnyBalance(change.carriedBalances);
  const price = (amount: number) => formatPrice(change.currency, amount, { cents: true });
  const per = change.billingPeriod === 'monthly' ? '/mo' : '/yr';

  return (
    <div className="planChange">
      <div className="planChange__inner">
        <div className="planChange__titleRow">
          <button
            type="button"
            className="planChange__back"
            onClick={onCancel}
            aria-label="Back to plans"
          >
            <Icon name="chevron-left" />
          </button>
          <h1 className="planChange__h1">Confirm your plan</h1>
        </div>

        <div className="planChange__card">
          <div className="planChange__cardHead">
            <div className="planChange__tiers">
              <span className="planChange__from">{fromPlan.label}</span>
              <Icon name="arrow-right" className="planChange__arrow" />
              <span className="planChange__to">{toPlan.label}</span>
            </div>
            <p className="planChange__price">
              {price(change.recurringPrice)}
              {per}
            </p>
          </div>

          {/* Trial: nothing has been charged, so there is nothing to pro-rate. */}
          {change.inTrial && (
            <div className="planChange__trial">
              <Icon name="check" className="planChange__trialIcon" />
              <div>
                <p className="planChange__trialLead">
                  You have not been charged, and switching plans now won’t change that.
                </p>
                <p className="planChange__trialBody">
                  First charge:{' '}
                  {price(change.firstChargeAmount ?? change.recurringPrice)} on{' '}
                  {change.nextChargeDate}.
                </p>
              </div>
            </div>
          )}

          {/* Money — its own row. */}
          {money && (
            <div className="planChange__row">
              <Icon name="credit" className="planChange__rowIcon" />
              <div className="planChange__rowBody">
                <span className="planChange__rowLabel">
                  Pro-rated credit for the unused part of this{' '}
                  {change.billingPeriod === 'monthly' ? 'month' : 'year'}
                </span>
                <span className="planChange__rowValue">−{price(money.unusedCredit)}</span>
              </div>
            </div>
          )}

          {/* Balances — a separate row, never a line inside the money block. */}
          {showCarryOver && (
            <div className="planChange__row">
              <Icon name="carry" className="planChange__rowIcon" />
              <p className="planChange__carryText">
                {carryOverSentence(change.carriedBalances)}
              </p>
            </div>
          )}

          {money && (
            <div className="planChange__due">
              <div className="planChange__dueRow">
                <span className="u-meta">Charged today</span>
                <span className="planChange__dueValue">{price(money.dueToday)}</span>
              </div>

              {money.rollsToNextInvoice > 0 && (
                <p className="planChange__extra">
                  The remaining credit applies to your {change.nextChargeDate} invoice.
                </p>
              )}

              <p className="planChange__next">
                <Icon name="calendar" />
                Then {price(change.recurringPrice)}
                {per} — next full charge {change.nextChargeDate}
              </p>
            </div>
          )}
        </div>

        <button type="button" className="planChange__confirm" onClick={onConfirm}>
          Confirm plan change
        </button>
        <button type="button" className="planChange__keep" onClick={onCancel}>
          Keep my current plan
        </button>
      </div>
    </div>
  );
}

/**
 * Near-verbatim from `BUSINESS_RULES.md` rule 4:
 * "Your 140 unused Ask Aware questions and 320 unused Aware Explain carry over."
 *
 * "questions" for Ask Aware, plain counts for Aware Explain. Never "tokens", never "credits"
 * as a generic term — "credit" belongs to the money line only.
 */
function carryOverSentence(balances: {
  askAwareQuestions: number | null;
  awareExplainUses: number | null;
}): string {
  const parts: string[] = [];
  if (balances.askAwareQuestions !== null) {
    parts.push(`${balances.askAwareQuestions.toLocaleString('en-US')} unused Ask Aware questions`);
  }
  if (balances.awareExplainUses !== null) {
    parts.push(`${balances.awareExplainUses.toLocaleString('en-US')} unused Aware Explain`);
  }
  return `Your ${parts.join(' and ')} carry over.`;
}
